/** Account settings — password reset + appearance (dark mode) preference. */
import { FormEvent, useState } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { api } from '../lib/api';
import { ThemePreference, getThemePreference, setThemePreference } from '../lib/theme';
import InlineError from '../components/InlineError';
import PasswordInput from '../components/PasswordInput';

function PasswordCard() {
  const [open, setOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api('/auth/password', {
        method: 'PUT',
        body: JSON.stringify({ newPassword, confirmPassword }),
      });
      setNewPassword('');
      setConfirmPassword('');
      setOpen(false);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update your password right now.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>Password</h2>
      {!open ? (
        <>
          {done && <p role="status">Your password has been updated.</p>}
          <p style={{ marginTop: 12 }}>
            <button onClick={() => { setDone(false); setOpen(true); }} style={{ width: 'auto' }}>
              Reset Password
            </button>
          </p>
        </>
      ) : (
        <form onSubmit={onSubmit}>
          <label>
            New password
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          </label>
          <label>
            Confirm new password
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="Re-enter your new password"
            />
          </label>
          {error && <InlineError message={error} />}
          <p style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button type="submit" disabled={busy || !newPassword || !confirmPassword} style={{ width: 'auto' }}>
              {busy ? 'Updating…' : 'Update password'}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setError(null); setNewPassword(''); setConfirmPassword(''); }}
              disabled={busy}
              style={{ width: 'auto' }}
            >
              Cancel
            </button>
          </p>
        </form>
      )}
    </div>
  );
}

const THEME_OPTIONS: { value: ThemePreference; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'system', label: 'System', Icon: Monitor },
  { value: 'dark', label: 'Dark', Icon: Moon },
];

function AppearanceCard() {
  const [pref, setPref] = useState<ThemePreference>(() => getThemePreference());

  function choose(next: ThemePreference) {
    setThemePreference(next);
    setPref(next);
  }

  return (
    <div className="card">
      <h2>Appearance</h2>
      <p style={{ marginBottom: 12 }}>Choose how PPPToo looks on this device.</p>
      <div className="phasetabs" role="radiogroup" aria-label="Theme">
        {THEME_OPTIONS.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={pref === value}
            className={`tab${pref === value ? ' active' : ''}`}
            onClick={() => choose(value)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  return (
    <>
      <h1 style={{ marginBottom: 10 }}>Settings</h1>
      <AppearanceCard />
      <PasswordCard />
    </>
  );
}
